import { formatForecastName } from './formatForecastName';
/**
 * Helper to create a unique map key for forecast data
 * Using underscore as delimiter because forecastId may contain hyphens
 * @param {string|number} forecastId - The forecast ID
 * @param {string} weekDay - The week day (MON, TUE, etc.)
 * @param {string|number} zoneId - The zone ID
 * @returns {string} The unique key
 */
export const createMapKey = (forecastId, weekDay, zoneId) => `${forecastId}_${weekDay}_${zoneId}`;

/**
 * Parse a map key back to its components
 * Format: forecastId_weekDay_zoneId (weekDay is always 3 chars: MON, TUE, etc.)
 * @param {string} key - The map key
 * @returns {{forecastId: string, weekDay: string, zoneId: string}}
 */
export const parseMapKey = (key) => {
  if (!key) return { forecastId: null, weekDay: null, zoneId: null };
  const [forecastId, weekDay, zoneId] = key.split('_');
  return { forecastId, weekDay, zoneId };
};

/**
 * Create a flat Map from forecast API data
 * Key format: `${forecastId}_${weekDay}_${zoneId}`
 * Value: full item data including zone info and day-specific data
 *
 * @param {Array} forecasts - Array of forecast objects from API
 * @returns {Map} Map with key -> item data
 */
export const createForecastDataMap = (forecasts) => {
  const dataMap = new Map();

  if (!forecasts || forecasts.length === 0) return dataMap;

  forecasts.forEach((forecast) => {
    const { week_day, groups: items, id: forecastId, ticket_type } = forecast;

    if (!forecastId || !week_day || !items || !Array.isArray(items)) return;

    items.forEach((item) => {
      const { id: zoneId, name, warehouse_id, boundary, zone_names, ...dayData } = item;

      if (!zoneId) return;

      const key = createMapKey(forecastId, week_day, zoneId);

      dataMap.set(key, {
        // Key components for easy access
        forecastId,
        weekDay: week_day,
        zoneId,
        ticketType: ticket_type,
        // Zone-level data (same across all weekdays for a zone)
        name: formatForecastName(name),
        warehouse_id,
        boundary,
        zone_names,
        // Day-specific data
        ...dayData,
      });
    });
  });

  return dataMap;
};

/**
 * Get unique zone IDs for a specific ticket type
 * Also returns zone info (name, warehouse_id, etc.) for each zone
 *
 * @param {Array} forecasts - Array of forecast objects from API
 * @param {string|null} ticketType - The ticket type to filter by, or null for all
 * @returns {Array<{id: string, name: string, warehouse_id: string, boundary: object, zone_names: Array}>}
 */
export const getZoneInfoForTicketType = (forecasts, ticketType) => {
  const zoneMap = new Map(); // zoneId -> zone info

  if (!forecasts || forecasts.length === 0) return [];

  forecasts.forEach((forecast) => {
    // Filter by ticket type if specified
    if (ticketType && forecast.ticket_type !== ticketType) return;

    const { groups: items, id: forecastId, week_day } = forecast;
    if (!items || !Array.isArray(items)) return;

    items.forEach((item) => {
      const { id: zoneId, name, warehouse_id, boundary, zone_names } = item;

      if (!zoneId) return;

      if (zoneMap.has(zoneId)) {
        zoneMap.get(zoneId).forecasts.push({ forecastId, week_day });
      } else {
        zoneMap.set(zoneId, {
          id: zoneId,
          name: formatForecastName(name),
          warehouse_id,
          boundary,
          zone_names,
          forecasts: [{ forecastId, week_day }],
        });
      }
    });
  });

  return Array.from(zoneMap.values());
};

/**
 * Get forecastId mapping by weekDay for a specific ticket type
 *
 * @param {Array} forecasts - Array of forecast objects from API
 * @param {string|null} ticketType - The ticket type to filter by, or null for all
 * @returns {Map<string, string>} Map of weekDay -> forecastId
 */
export const getForecastIdByWeekDay = (forecasts, ticketType) => {
  const forecastIdMap = new Map();

  if (!forecasts || forecasts.length === 0) return forecastIdMap;

  forecasts.forEach((forecast) => {
    // Filter by ticket type if specified
    if (ticketType && forecast.ticket_type !== ticketType) return;

    const { week_day, id: forecastId } = forecast;
    if (!week_day || !forecastId) return;

    // Only set if not already set (first one wins)
    if (!forecastIdMap.has(week_day)) {
      forecastIdMap.set(week_day, forecastId);
    }
  });

  return forecastIdMap;
};

/**
 * Clone a Map (shallow clone of values)
 * @param {Map} sourceMap - The source Map to clone
 * @returns {Map} A new Map with cloned entries
 */
export const cloneMap = (sourceMap) => {
  const newMap = new Map();
  sourceMap.forEach((value, key) => {
    newMap.set(key, { ...value });
  });
  return newMap;
};
